import moment from 'moment';
import { useCallback, useContext, useRef, useState } from 'react';

import { DEFAULT_HEADER, LABELS } from '../../../../constants/reports/reports-data';
import { VOL_SHOCK_LIST } from '../../../../constants/reports/reports-filters';
import {
  DATA_DISPLAY_KEYS,
  PortfolioManagementContext,
} from '../../../../providers/portfolio-management';
import {
  RiskFiltersContext,
  riskTypes,
} from '../../../../providers/risk-management/risk-filters-provider';
import { RiskManagementContext } from '../../../../providers/risk-management/risk-management-provider';
import Utils from '../../../../utils/immersive_utils';
import {
  handlePerpetualsAggregated,
  handleOptionsAggregated,
  handleFuturesAggregated,
  handleCashAggregated,
} from '../../helpers/risk-data-aggregated';
import {
  handlePerpetualsPerAccount,
  handleOptionsPerAccount,
  handleFuturesPerAccount,
  handleCashPerAccount,
} from '../../helpers/risk-data-per-account';
import { selectMetricRisk } from '../../helpers/risk-metric';

interface RiskDataProps {
  matrix: 'SPOT_LADDER' | 'SPOT_GRID' | 'SPOT_TIME';
}

// Interim guard - the backend emits future horizons (e.g. 1D..5D, the TIME_LIST
// values) as top-level result keys alongside the real token pairs; proper fix is
// backend-side (IM-2726 notes). Real pairs like BTCUSD/ETHUSD never match.
export const isHorizonDateToken = (token: string) => /^\d+D$/.test(token);

export const useRiskData = ({ matrix }: RiskDataProps) => {
  const [header, setHeader] = useState<any[]>([]);
  const {
    filters: {
      token,
      setToken,
      setTokenList,
      riskMetric,
      venueType,
      volShock,
      time,
      setTimeList,
      setVolShockList,
      riskInstType,
    },
  } = useContext(RiskFiltersContext);

  const spotValuesRef = useRef<number[]>([]);

  const { selectedDataDisplayOption } = useContext(PortfolioManagementContext);

  const isDataDisplayPerAccount = selectedDataDisplayOption === DATA_DISPLAY_KEYS.perAccount;

  const { setData, setDataGraph } = useContext(RiskManagementContext);

  const handleData = useCallback(
    (objData: any, riskMetricsParam?: string[]) => {
      try {
        const handleNoData = () => {
          setData([]);
          setHeader(DEFAULT_HEADER);
        };

        const handleGraphData = (tokenData: any[]) => {
          const processedData: any[] = [];

          const tokenDataResult = tokenData[venueType]?.result;

          tokenDataResult?.forEach((item: any) => {
            const { horizon_date, vol_shock, report } = item;
            if (vol_shock.toString() === volShock) {
              const data = report.risks.map((risk: any) => {
                const totalRisk = risk.breakdown.find(
                  (b: any) => b.instrument === 'TOTAL' && b.contract === 'ALL',
                );

                const values = totalRisk ? totalRisk.risk : [];

                return {
                  values: values,
                  vol_shock,
                  id: risk.risk_type,
                };
              });
              processedData.push({
                horizon_date,
                data,
              });
            }
          });

          setDataGraph(processedData);
        };

        const handleTokenList = (tokens: string[]) => {
          const getLabel = (token: string) => {
            const suffixes = ['USDC', 'USDT', 'USD'];

            for (const suffix of suffixes) {
              if (token.endsWith(suffix)) {
                const baseToken = token.slice(0, -suffix.length);
                return `${baseToken} ${suffix}`;
              }
            }

            return token;
          };

          const realTokens = tokens.filter((tk) => !isHorizonDateToken(tk));
          const sortedTokens = Utils.naturalSort(realTokens);

          setTokenList(
            sortedTokens.map((tk) => ({
              value: tk,
              label: getLabel(tk),
              isVisible: true,
            })),
          );
        };

        const updateVisibilityList = (
          setList: React.Dispatch<React.SetStateAction<any>>,
          key: string,
          tokenData: any[],
          source?: any[],
        ) => {
          setList((prev: any[]) =>
            (source || prev).map((item: any) => ({
              ...item,
              isVisible: tokenData.some((data: any) => data[key]?.toString() === item.value),
            })),
          );
        };

        const handleTimeList = (tokenData: any[]) => {
          updateVisibilityList(setTimeList, 'horizon_date', tokenData);
        };

        const handleVolShockList = (tokenData: any[]) => {
          updateVisibilityList(setVolShockList, 'vol_shock', tokenData, VOL_SHOCK_LIST);
        };

        const selectHandler = (
          perAccountHandler: any,
          aggregatedHandler: any,
          riskInstType?: string,
        ) => {
          return (...args: any) =>
            isDataDisplayPerAccount
              ? perAccountHandler(...args, riskInstType)
              : aggregatedHandler(...args, riskInstType);
        };

        const handleReportRisks = (reportRisks: any, isAccountsAggregated = false) => {
          const risksData: any = {};

          const instrumentHandlers = {
            'PERPETUAL (INVERSE)': selectHandler(
              handlePerpetualsPerAccount,
              handlePerpetualsAggregated,
              'INVERSE',
            ),
            'PERPETUAL (LINEAR)': selectHandler(
              handlePerpetualsPerAccount,
              handlePerpetualsAggregated,
              'LINEAR',
            ),
            FUTURE: selectHandler(handleFuturesPerAccount, handleFuturesAggregated),
            OPTION: selectHandler(handleOptionsPerAccount, handleOptionsAggregated),
            CASH: selectHandler(handleCashPerAccount, handleCashAggregated),
          };

          const verifyInstrumentBreakdown = (breakdown: any) => {
            const hasAnyBooks = breakdown.some((bk: any) => bk.book);

            return isDataDisplayPerAccount || (!isDataDisplayPerAccount && !hasAnyBooks)
              ? 'TOTAL'
              : 'ALL';
          };

          const handleRiskData = (risk: any, exchange: string) => {
            const riskTypeKey = risk.risk_type;
            const totalBreakdown = risk.breakdown.find(
              (item: any) =>
                item.instrument === `${verifyInstrumentBreakdown(risk.breakdown)}` &&
                item.contract === 'ALL',
            );
            const breakdownValues = totalBreakdown?.risk || [];

            if (!risksData[riskTypeKey]) {
              risksData[riskTypeKey] = {
                id: riskTypeKey,
                label: LABELS[riskTypeKey],
                values: Array(breakdownValues.length).fill(0),
                children: [],
              };
            }

            const children = [];

            for (const [instrumentType, handler] of Object.entries(instrumentHandlers)) {
              if (riskInstType.includes(instrumentType)) {
                const instrumentData = handler(riskTypeKey, risk.breakdown);
                if (instrumentData) {
                  if (Array.isArray(instrumentData)) {
                    instrumentData.forEach((data) => {
                      children.push(data);
                      data.values.forEach((value: number, index: number) => {
                        risksData[riskTypeKey].values[index] += value;
                      });
                    });
                  } else {
                    children.push(instrumentData);
                    instrumentData.values.forEach((value: number, index: number) => {
                      risksData[riskTypeKey].values[index] += value;
                    });
                  }
                }
              }
            }

            const filteredChildren = children.filter((item) => !!item);

            if (isAccountsAggregated) {
              risksData[riskTypeKey].children.push({
                id: `${exchange.toUpperCase()}_${riskTypeKey}`,
                label: exchange.toUpperCase(),
                values: breakdownValues,
                children: filteredChildren,
              });
            } else {
              risksData[riskTypeKey].children.push(...filteredChildren);
            }
          };

          if (isAccountsAggregated) {
            for (const exchangeKey in reportRisks) {
              const exchangeData = reportRisks[exchangeKey];
              if (exchangeData && exchangeData.risks) {
                exchangeData.risks.forEach((risk: any) => {
                  handleRiskData(risk, exchangeData.exchange || exchangeKey);
                });
              }
            }
          } else {
            reportRisks?.forEach((risk: any) => {
              if (!risk) return;
              handleRiskData(risk, venueType);
            });
          }

          return risksData;
        };

        const handleVolShockGrid = (data: any, isAccountsAggregated: boolean) => {
          let header: any[] = [];
          let newData = [];

          const exchanges = Object.keys(data);
          let dataResult = null;

          for (const exchange of exchanges) {
            const exchangeData = data[exchange]?.result;
            if (exchangeData && exchangeData.length > 0) {
              dataResult = exchangeData;
              break;
            }
          }

          if (!dataResult) {
            handleNoData();
            return;
          }

          const spot = [
            {
              id: 'spot',
              label: 'Spot',
              values: spotValuesRef.current,
            },
          ];

          if (!isAccountsAggregated) {
            header = dataResult[0]?.report['spot_shock']?.map((it: number) => Math.round(it * 100));

            newData = dataResult?.map((item: any) => {
              const volShock = item?.vol_shock;
              let reportRisks = item?.report.risks;

              reportRisks = reportRisks.filter((item: any) => item?.risk_type === riskMetric);
              const risks = handleReportRisks(reportRisks);
              const metricRisk = selectMetricRisk(risks, riskMetric);

              return [
                {
                  id: volShock.toString(),
                  label: `${(volShock * 100).toString()}%`,
                  values: metricRisk.values,
                  children: metricRisk.children,
                },
              ];
            });

            const formatNewData = [spot, ...newData];
            setHeader(header);
            setData(formatNewData);
          } else {
            const exchangesData: { [key: string]: any[] } = {};

            for (const exchange of exchanges) {
              const exchangeResult = data[exchange]?.result;
              if (exchangeResult && exchangeResult.length > 0) {
                exchangesData[exchange] = exchangeResult;
              }
            }

            if (Object.keys(exchangesData).length === 0) {
              handleNoData();
              return;
            }

            const firstExchangeData = Object.values(exchangesData)[0];
            header = firstExchangeData[0].report.spot_shock.map((it: number) =>
              Math.round(it * 100),
            );

            const allVolShocksSet = new Set<number>();
            for (const exchange in exchangesData) {
              exchangesData[exchange].forEach((item: any) => {
                allVolShocksSet.add(item.vol_shock);
              });
            }
            const allVolShocks = Array.from(allVolShocksSet).sort((a, b) => a - b);

            const combinedAccounts = allVolShocks
              .map((volShock) => {
                let valuesTotals: number[] = [];
                const children = [];

                for (const exchange in exchangesData) {
                  const exchangeItem = exchangesData[exchange].find(
                    (item: any) => item.vol_shock === volShock,
                  );
                  if (exchangeItem) {
                    const exchangeRisks = handleReportRisks(
                      exchangeItem.report.risks.filter(
                        (item: any) => item.risk_type === riskMetric,
                      ),
                    );
                    const metricRisk = selectMetricRisk(exchangeRisks, riskMetric);
                    const exchangeValues = metricRisk.values;

                    if (valuesTotals.length === 0) {
                      valuesTotals = [...exchangeValues];
                    } else {
                      valuesTotals = valuesTotals.map(
                        (val: number, index: number) => val + exchangeValues[index],
                      );
                    }

                    children.push({
                      id: `${exchange.toUpperCase()}-${volShock}`,
                      label: exchange.toUpperCase(),
                      values: exchangeValues,
                      children: metricRisk.children,
                    });
                  }
                }

                if (children.length === 0) {
                  return null;
                }

                return [
                  {
                    id: volShock.toString(),
                    label: `${(volShock * 100).toString()}%`,
                    values: valuesTotals,
                    children: children,
                  },
                ];
              })
              .filter((item) => item !== null);

            newData = combinedAccounts;
            setHeader(header);
            setData([spot, ...newData]);
          }
        };

        const handleSpotTimeGrid = (data: any, isAccountsAggregated: boolean) => {
          let header: string[] = [];

          const orderDate = (offset: string) => {
            const currentDate = moment.utc();
            return currentDate.add(parseInt(offset.substring(0, 1)), 'days');
          };

          const spot = [
            {
              id: 'spot',
              label: 'Spot',
              values: spotValuesRef.current,
            },
          ];

          if (!isAccountsAggregated) {
            const exchanges = Object.keys(data);
            let dataArr = null;

            for (const exchange of exchanges) {
              const exchangeData = data[exchange]?.result;
              if (exchangeData && exchangeData.length > 0) {
                dataArr = exchangeData;
                break;
              }
            }

            if (!dataArr) {
              handleNoData();
              return;
            }

            header = dataArr[0]?.report['spot_shock']?.map((it: number) => Math.round(it * 100));

            dataArr?.sort((a: any, b: any) => {
              const dateA = orderDate(a['horizon_date']);
              const dateB = orderDate(b['horizon_date']);

              if (dateA.isBefore(dateB)) return -1;
              if (dateA.isAfter(dateB)) return 1;
              return 0;
            });

            const newData = dataArr.map((item: any) => {
              const volShock = item['vol_shock'];
              const horizonDate = item['horizon_date'];
              let reportRisks = item.report['risks'];

              reportRisks = reportRisks.filter((item: any) => item['risk_type'] === riskMetric);
              const risks = handleReportRisks(reportRisks);
              const metricRisk = selectMetricRisk(risks, riskMetric);

              return [
                {
                  id: `${volShock}_${horizonDate}`,
                  label: orderDate(horizonDate).format('YYYY-MM-DD'),
                  values: metricRisk.values,
                  children: metricRisk.children,
                },
              ];
            });

            const formatNewData = [spot, ...newData];

            setHeader(header);
            setData(formatNewData);
          } else {
            const exchanges = Object.keys(data);
            const exchangesData: { [key: string]: any[] } = {};

            for (const exchange of exchanges) {
              const exchangeResult = data[exchange]?.result;
              if (exchangeResult && exchangeResult.length > 0) {
                exchangesData[exchange] = exchangeResult;
              }
            }

            if (Object.keys(exchangesData).length === 0) {
              handleNoData();
              return;
            }

            const firstExchangeData = Object.values(exchangesData)[0];
            header = firstExchangeData[0].report.spot_shock.map((it: number) =>
              Math.round(it * 100),
            );

            const allHorizonDatesSet = new Set<string>();
            for (const exchange in exchangesData) {
              exchangesData[exchange].forEach((item: any) => {
                allHorizonDatesSet.add(item.horizon_date);
              });
            }
            const allHorizonDates = Array.from(allHorizonDatesSet);

            const combinedAccounts = allHorizonDates
              .map((horizonDate) => {
                let valuesTotals: number[] = [];
                const children: any[] = [];

                for (const exchange in exchangesData) {
                  const exchangeItem = exchangesData[exchange].find(
                    (item: any) => item.horizon_date === horizonDate,
                  );
                  if (exchangeItem) {
                    const exchangeRisks = handleReportRisks(
                      exchangeItem.report.risks.filter(
                        (item: any) => item.risk_type === riskMetric,
                      ),
                    );
                    const metricRisk = selectMetricRisk(exchangeRisks, riskMetric);
                    const exchangeValues = metricRisk.values;

                    if (valuesTotals.length === 0) {
                      valuesTotals = [...exchangeValues];
                    } else {
                      valuesTotals = valuesTotals.map(
                        (val: number, index: number) => val + exchangeValues[index],
                      );
                    }

                    children.push({
                      id: `${exchange.toUpperCase()}-${horizonDate}`,
                      label: exchange.toUpperCase(),
                      values: exchangeValues,
                      children: metricRisk.children,
                    });
                  }
                }

                if (children.length === 0) {
                  return null;
                }

                return [
                  {
                    id: `${volShock}_${horizonDate}`,
                    label: orderDate(horizonDate).format('YYYY-MM-DD'),
                    values: valuesTotals,
                    children: children,
                  },
                ];
              })
              .filter((item) => item !== null);

            const newData = combinedAccounts.sort((a: any, b: any) => {
              const dateA = orderDate(a[0].id);
              const dateB = orderDate(b[0].id);

              if (dateA.isBefore(dateB)) return -1;
              if (dateA.isAfter(dateB)) return 1;
              return 0;
            });

            setHeader(header);
            setData([spot, ...newData]);
          }
        };

        const handleLadder = (tokenData: any, isAccountsAggregated: boolean) => {
          if (!tokenData) {
            handleNoData();
            return;
          }

          let dataFound = false;

          for (const exchange of Object.keys(tokenData)) {
            if (tokenData[exchange]?.result && tokenData[exchange].result.length > 0) {
              handleTimeList(tokenData[exchange].result);
              handleVolShockList(tokenData[exchange].result);
              dataFound = true;
              break;
            }
          }

          if (!dataFound) {
            handleNoData();
            return;
          }

          const findDataForTimeAndShock = (data: any) =>
            data?.find(
              (item: any) => item.horizon_date === time && item.vol_shock.toString() === volShock,
            );

          const getRisksData = (report: any, isAggregated: boolean) => {
            if (isAggregated) {
              const aggregatedRisks: any = {};
              let dataAvailable = false;

              for (const exchange of Object.keys(tokenData)) {
                const exchangeData = tokenData[exchange];
                const data = exchangeData
                  ? findDataForTimeAndShock(exchangeData.result)
                  : undefined;

                if (data) {
                  dataAvailable = true;
                  aggregatedRisks[exchange] = { risks: data.report.risks, exchange };
                }
              }

              if (!dataAvailable) {
                handleNoData();
                return {};
              }

              return handleReportRisks(aggregatedRisks, true);
            } else {
              return handleReportRisks(report.report.risks);
            }
          };

          let risks: any = {};
          let report = null;

          const spot = [
            {
              id: 'spot',
              label: 'Spot',
              values: spotValuesRef.current,
            },
          ];

          if (isAccountsAggregated) {
            let dataFound = false;
            let spot_shock = null;

            for (const exchange of Object.keys(tokenData)) {
              const exchangeData = tokenData[exchange];
              const data = exchangeData ? findDataForTimeAndShock(exchangeData.result) : undefined;

              if (data && data.report && data.report.spot_shock) {
                dataFound = true;
                spot_shock = data.report.spot_shock;
                break;
              }
            }

            if (!dataFound) {
              handleNoData();
              return;
            }

            report = {
              report: {
                spot_shock: spot_shock,
              },
            };

            risks = getRisksData(report, true);
          } else {
            let reportFound = false;
            for (const exchange of Object.keys(tokenData)) {
              const exchangeData = tokenData[exchange];
              if (exchangeData && exchangeData.result && exchangeData.result.length > 0) {
                const data = findDataForTimeAndShock(exchangeData.result);
                if (data && data.report && data.report.risks) {
                  report = data;
                  reportFound = true;
                  break;
                }
              }
            }

            if (!reportFound) {
              handleNoData();
              return;
            }

            risks = getRisksData(report, false);
          }

          const blocks = [
            spot,
            ...Object.keys(LABELS)
              .filter((key: string) => risks[key] && Object.keys(risks[key]).length > 0)
              .filter(
                (key: string) =>
                  riskMetricsParam &&
                  riskMetricsParam?.length > 0 &&
                  riskMetricsParam.includes(key),
              )
              .map((key: string) => [risks[key]]),
          ];

          setHeader(report.report.spot_shock.map((it: number) => Math.round(it * 100)));
          setData(blocks);
        };

        const getHandleFn = () => {
          const functions = {
            [riskTypes.spotGrid]:
              matrix === riskTypes.spotTime ? handleSpotTimeGrid : handleVolShockGrid,
            [riskTypes.spotTime]:
              matrix === riskTypes.spotTime ? handleSpotTimeGrid : handleVolShockGrid,
            [riskTypes.spotLadder]: handleLadder,
          };

          return functions[matrix];
        };

        if (isDataDisplayPerAccount) {
          if (!objData.data[venueType] || !objData.data[venueType].result) {
            handleNoData();
            return;
          }
        }

        let tokenData: any;
        const result = objData.data[venueType]?.result;

        const exchanges = Object.keys(objData.data);

        const isDataAggregatedAccounts = !isDataDisplayPerAccount && exchanges.length > 1;

        const tokens = isDataDisplayPerAccount
          ? Object.keys(result)
          : exchanges
              .flatMap((exchange) => Object.keys(objData.data[exchange]))
              .filter((item, index, arr) => arr.indexOf(item) === index);

        if (isDataAggregatedAccounts) {
          tokenData = {};

          exchanges.forEach((exchange) => {
            const exchangeTokens = objData.data[exchange];
            if (exchangeTokens && exchangeTokens[token]) {
              tokenData[exchange] = {
                result: exchangeTokens[token],
                exchange,
              };
            }
          });

          const tokensPerExchange = exchanges.map((exchange) => {
            const tokens = Object.keys(objData.data[exchange]);
            return tokens.filter((item, index) => tokens.indexOf(item) === index);
          });

          const commonTokens = tokensPerExchange.reduce((commonArray, exchangeArray) => {
            return commonArray.filter((token) => exchangeArray.includes(token));
          });

          const commonTokensArray = [...commonTokens];

          handleTokenList(commonTokensArray);

          if (!commonTokensArray.includes(token)) {
            setToken(commonTokensArray[0] || 'BTCUSD');
            return;
          }
        } else {
          if (!isDataDisplayPerAccount) {
            let hasData = false;
            const allTokensArray: any[] = [];

            tokenData = {};

            for (const exchange of exchanges) {
              const exchangeData = objData.data[exchange];
              if (exchangeData && Object.keys(exchangeData).length > 0) {
                const exchangeResult = exchangeData[token];
                if (exchangeResult) {
                  tokenData[exchange] = { result: exchangeResult, exchange };
                  hasData = true;
                }
                const exchangeTokens = Object.keys(exchangeData).filter(
                  (tokenParam) => exchangeData[tokenParam].length > 0,
                );

                exchangeTokens.forEach((tk) => {
                  if (!allTokensArray.includes(tk)) {
                    allTokensArray.push(tk);
                  }
                });
              }
            }

            if (!hasData) {
              handleNoData();
              return;
            }

            handleTokenList(allTokensArray);
          } else {
            tokenData = {
              [venueType]: { result: result[token], exchange: venueType },
            };
            handleTokenList(tokens);
          }
        }

        if (!tokenData || Object.keys(tokenData).length === 0) {
          handleNoData();
          return;
        }

        const handleFn = getHandleFn();
        handleFn(tokenData, isDataAggregatedAccounts);

        if (isDataDisplayPerAccount) {
          handleGraphData(tokenData);
        }
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    [
      time,
      token,
      volShock,
      riskMetric,
      venueType,
      isDataDisplayPerAccount,
      riskInstType,
      selectedDataDisplayOption,
    ],
  );

  return {
    handleData,
    header,
    spotValuesRef,
  };
};
